import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, forwardRef, HostListener, Input, Output, Provider } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IconComponent } from '../icon/icon.component';

export interface MultiSelectOption {
  [key: string]: any;
}

const MULTI_SELECT_VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MultiSelectComponent),
  multi: true,
};

@Component({
  selector: 'l-multi-select',
  standalone: true,
  templateUrl: './multi-select.component.html',
  imports: [CommonModule, IconComponent],
  providers: [MULTI_SELECT_VALUE_ACCESSOR]
})
export class MultiSelectComponent implements ControlValueAccessor {

  //#region Constructor

  constructor(private readonly elementRef: ElementRef) {}

  //#endregion

  //#region Public Properties

  @Input({ required: true })
  public options: MultiSelectOption[] = [];

  @Input({ required: true })
  public optionLabel: string = '';

  @Input()
  public placeholder: string = 'Selecione';

  @Input()
  public allOptionLabel: string = 'Todos/as';

  @Input()
  public showClear: boolean = false;

  @Output()
  public panelShow: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  public panelHide: EventEmitter<void> = new EventEmitter<void>();

  public isOpen: boolean = false;

  public selectedItems: MultiSelectOption[] = [];

  public disabled: boolean = false;

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closePanel();
    }
  }

  //#endregion

  //#region Public Methods

  public togglePanel(): void {
    if (this.disabled) {
      return;
    }
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.panelShow.emit();
    } else {
      this.panelHide.emit();
    }
  }

  public closePanel(): void {
    if (this.isOpen) {
      this.isOpen = false;
      this.panelHide.emit();
    }
  }

  public toggleAll(): void {
    if (this.isAllSelected()) {
      this.selectedItems = [];
    } else {
      this.selectedItems = [...this.options];
    }

    this.updateValue();
  }

  public isAllSelected(): boolean {
    return this.options.length > 0 && this.selectedItems.length === this.options.length;
  }

  public toggleOption(option: MultiSelectOption): void {
    const index = this.selectedItems.findIndex(item => this.compareOptions(item, option));
    if (index > -1) {
      this.selectedItems = this.selectedItems.filter((_, i) => i !== index);
    } else {
      this.selectedItems = [...this.selectedItems, option];
    }
    this.updateValue();
  }

  public isSelected(option: MultiSelectOption): boolean {
    return this.selectedItems.some(item => this.compareOptions(item, option));
  }

  public clear(): void {
    this.selectedItems = [];
    this.updateValue();
  }

  public getDisplayLabel(): string {
    if (this.selectedItems.length === 0) {
      return this.placeholder;
    }
    if (this.isAllSelected()) {
      return this.allOptionLabel;
    }
    if (this.selectedItems.length === 1) {
      return this.getOptionLabel(this.selectedItems[0]);
    }
    return `${this.selectedItems.length} selecionados`;
  }

  public getOptionLabel(option: MultiSelectOption): string {
    return option[this.optionLabel] ?? '';
  }

  public writeValue(value: MultiSelectOption[] | null): void {
    this.selectedItems = value ?? [];
  }

  public registerOnChange(fn: (value: MultiSelectOption[] | null) => void): void {
    this.propagateChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.propagateTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  //#endregion

  //#region Private Methods

  private propagateChange: (value: MultiSelectOption[] | null) => void = () => {
    // Placeholder
  };

  private propagateTouched: () => void = () => {
    // Placeholder
  };

  private compareOptions(item: MultiSelectOption, option: MultiSelectOption): boolean {
    return (item['id'] && option['id']) ? item['id'] === option['id'] : item === option;
  }

  private updateValue(): void {
    this.propagateChange(this.selectedItems.length > 0 ? this.selectedItems : null);
    this.propagateTouched();
  }

  //#endregion

}
